// ScrollManager — platform-wide scroll behavior.
// PUSH navigations (clicking into a new page) start at the top.
// POP navigations (browser back/forward) restore the previous position,
// using the browser's native history.scrollRestoration.
import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

export default function ScrollManager() {
  const { pathname, search } = useLocation()
  const key = `${pathname}${search}`

  // 1. Let the browser own back/forward scroll restoration.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'auto'
    }
  }, [])

  // 2. Distinguish Push from Pop: a Pop navigation arrives with the
  //    browser having already restored (or about to restore) position,
  //    and React Router re-renders the same component instance for it.
  //    We track the last location we handled — if the browser reports it
  //    handled this key via popstate, we skip the top-scroll.
  const lastKeyRef = useRef<string | null>(null)
  const isPopRef = useRef(false)

  useEffect(() => {
    const onPopState = () => { isPopRef.current = true }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  useLayoutEffect(() => {
    if (lastKeyRef.current === key) return
    lastKeyRef.current = key

    if (isPopRef.current) {
      isPopRef.current = false   // back/forward: browser restores position
      return
    }
    window.scrollTo(0, 0)        // new destination: start at the top
  }, [key])

  return null
}